import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import PageContainer from "@/v3-dashboard/components/UI/PageContainer"
import PageHeader from "@/v3-dashboard/components/UI/PageHeader"
import { useChatbotStore } from "@/stores/chatbot/chatbotStore"
import LayoutRightSidebar from "@/layouts/LayoutRightSidebar"
import Select from '@/shared/components/UI/Select';
import Link from '@/v3-dashboard/components/UI/Link';
import ArrowUpRight from '@/shared/icons/ArrowUpRight';
import clsx from 'clsx';
import * as chatbotService from '@/services/chatbot.service';
import * as deploymentService from '@/services/deployment.service';
import LLMSelectorSimple from '@/v3-dashboard/components/UI/LLMSelectorSimple';
import InfoBanner from '@/v3-dashboard/components/UI/InfoBanner';
import PageFooterActions from '@/v3-dashboard/components/UI/PageFooterActions';
import BubblePreviewWrapper from '@/components/BubblePreviewWrapper';
import useCustomizationData from '@/hooks/useCustomization';
import { useUserStore } from '@/stores/user/userStore';
import useLayoutStore from '@/stores/layout/layoutStore';
import { useJoyrideStore } from '@/stores/joyride/joyrideStore';
import useIsAboveBreakpoint from '@/helpers/useIsAboveBreakpoint';
import mixpanel, { getOnboardingSessionProps } from '@/utils/mixpanel';
import useChatbotOnboardingActions from '@/hooks/useChatbotOnboardingActions';
import DLoading from '@/components/DLoading';
import DTooltip from '@/components/Global/DTooltip';
import IconWrapper from '@/shared/components/UI/IconWrapper';
import InfoIcon from '@/shared/icons/InfoIcon';
import Textarea from '@/shared/components/UI/Textarea';
import useToast from '@/hooks/useToast';
import DismissableExplainerCard from '@/v3-dashboard/components/UI/DismissableExplainerCard';
import useExplainerCard from '@/hooks/useExplainerCard';

const FIRST_TIME_LOADER_MS = 1500;

const Preview = () => {
    const params = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedChatbot = useChatbotStore((state) => state.selectedChatbot);
    const { auth } = useUserStore((state) => state);
    const { customizationData, loading } = useCustomizationData(true, params.id);
    const setPreviewBubblePage = useLayoutStore((state) => state.setPreviewBubblePage);
    const { addSuccessToast, addErrorToast } = useToast();
    const { currentStepId } = useChatbotOnboardingActions(params.id);
    const { isVisible: showExplainerCard, dismiss: dismissExplainerCard } = useExplainerCard('preview', params.id);

    const messageIndexRef = useRef(0)
    const currentStepIdRef = useRef(currentStepId)
    useEffect(() => { currentStepIdRef.current = currentStepId }, [currentStepId])

    const startTour = useJoyrideStore((state) => state.startTour);
    const stopTour = useJoyrideStore((state) => state.stopTour);
    const hasStartedPreviewTourRef = useRef(false);
    const isAbovePreview = useIsAboveBreakpoint('preview');

    // Personalities state
    const [personalities, setPersonalities] = useState([]);
    const [isLoadingPersonalities, setIsLoadingPersonalities] = useState(false);
    const [selectedPersonality, setSelectedPersonality] = useState(null);
    const [personalityError, setPersonalityError] = useState('');
    const [personalityContent, setPersonalityContent] = useState(null);
    const [creativityLevel, setCreativityLevel] = useState(50);
    const [enableGeneralKnowledge, setEnableGeneralKnowledge] = useState(false);
    const [initialPersonalityValues, setInitialPersonalityValues] = useState(null);
    const [isSavingPersonality, setIsSavingPersonality] = useState(false);
    
    // LLM state - initialize with chatbot's current model
    const [selectedLLM, setSelectedLLM] = useState(() => {
      const initialValue = selectedChatbot?.last_model_used?.value || selectedChatbot?.llmModel?.value;
      return initialValue || null;
    });
    
    // Deployment combo check state
    const [nrMatches, setNrMatches] = useState(0);
    const [isCheckingCombo, setIsCheckingCombo] = useState(true);
    const selectedPersonalityData = personalities.find((p) => p.id === selectedPersonality);
    const hasPersonalityChanges = Boolean(
      selectedPersonality &&
      initialPersonalityValues &&
      (
        (personalityContent ?? '') !== (initialPersonalityValues.personalityContent ?? '') ||
        creativityLevel !== initialPersonalityValues.creativityLevel ||
        enableGeneralKnowledge !== initialPersonalityValues.enableGeneralKnowledge
      )
    );

    const handleMessageSent = useCallback(() => {
      mixpanel.track('Preview Message Sent', {
        chatbot_id: params.id,
        message_index: messageIndexRef.current,
        onboarding_step: currentStepIdRef.current || null,
        ...getOnboardingSessionProps(),
      })
      messageIndexRef.current += 1
    }, [params.id])

    const handleCreativityChange = (e) => {
      setCreativityLevel(parseInt(e.target.value, 10));
    };

    const handleBehaviorInstructionsChange = (e) => {
      setPersonalityContent(e.target.value);
    };

    const handleGetHelpClick = () => {
      window.parent.postMessage({
        eventType: 'tooltipPromptClick',
        eventData: 'I need help with behavior instructions'
      }, '*');
    };

    const handleSavePersonality = async () => {
      if (!selectedPersonality) return;

      if (!personalityContent?.trim()) {
        addErrorToast({ message: 'Behavior Instructions is required' });
        return;
      }

      try {
        setIsSavingPersonality(true);
        await chatbotService.updatePersonalities(
          selectedPersonality,
          personalityContent,
          selectedPersonalityData?.personality_name || null,
          (creativityLevel / 100).toFixed(2),
          enableGeneralKnowledge,
          selectedPersonalityData?.personality_explanation || ''
        );
        setInitialPersonalityValues({
          personalityContent: personalityContent ?? '',
          creativityLevel,
          enableGeneralKnowledge,
        });
        addSuccessToast({ message: 'Personality updated successfully' });
      } catch (error) {
        console.error('Failed to update personality from preview:', error);
        addErrorToast({
          message: error?.response?.data?.detail || error?.message || 'Failed to update personality'
        });
      } finally {
        setIsSavingPersonality(false);
      }
    };
    // First-time onboarding: show loader so topbar/layout can paint for a smoother experience
    const isFirstTime = searchParams.get('first_time') === 'true';
    const [showFirstTimeLoader, setShowFirstTimeLoader] = useState(() => isFirstTime);
    const firstTimeLoaderStarted = useRef(false);

    // First-time: keep loader visible for a minimum time so topbar/layout is on screen
    useEffect(() => {
      if (!isFirstTime || !showFirstTimeLoader || firstTimeLoaderStarted.current) return;
      firstTimeLoaderStarted.current = true;
      const t = setTimeout(() => {
        setShowFirstTimeLoader(false);
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          return next;
        }, { replace: true });
      }, FIRST_TIME_LOADER_MS);
      return () => clearTimeout(t);
    }, [isFirstTime, showFirstTimeLoader, setSearchParams]);

    // Set preview bubble page state for layout
    useEffect(() => {
      mixpanel.track('Chatbot Preview Viewed', {
        chatbot_id: params.id,
        ...getOnboardingSessionProps(),
      });
      setPreviewBubblePage({ inPage: true, isOpen: false });

      return () => {
        setPreviewBubblePage({ inPage: false, isOpen: false });
      };
    }, [setPreviewBubblePage]);

    // Stop the tour if the screen drops below the preview breakpoint (panel hidden, button shown)
    useEffect(() => {
      if (!isAbovePreview) {
        stopTour();
      }
    }, [isAbovePreview, stopTour]);

    useEffect(() => {
      const shouldStartPreviewTour = isFirstTime || currentStepId === 'test_chatbot';
      const shouldBlockStart =
        !shouldStartPreviewTour ||
        showFirstTimeLoader ||
        isCheckingCombo ||
        hasStartedPreviewTourRef.current ||
        !isAbovePreview;

      // Wait until loaders are gone so tour targets exist.
      if (shouldBlockStart) {
        return;
      }

      hasStartedPreviewTourRef.current = true;

      // Temporarily disabled - comment back in to restore the Joyride "Send a message" tooltip
      // const timer = setTimeout(() => {
      //   startTour({
      //     key: 'preview-onboarding',
      //     chatbotId: params.id,
      //     steps: [
      //       {
      //         target: '[data-tour-id="preview-bubble"]',
      //         content: <span>You&apos;re all set! Experience your agent in this live preview exactly as your visitors will. <strong>Send a message</strong> to try it out.</span>,
      //         tourKey: 'preview-onboarding',
      //         disableBeacon: true,
      //         spotlightClicks: true,
      //         placement: 'left',
      //         hideFooter: true,
      //       },
      //     ],
      //   });
      // }, 1000);

      // return () => {
      //   clearTimeout(timer);
      // };
    }, [isFirstTime, currentStepId, showFirstTimeLoader, isCheckingCombo, startTour, isAbovePreview, params.id]);

    // Sync selectedLLM with chatbot's current model when chatbot loads/changes
    useEffect(() => {
      if (selectedChatbot) {
        const modelValue = selectedChatbot.last_model_used?.value || selectedChatbot.llmModel?.value;
        
        // Only update if different to avoid infinite loops
        if (modelValue && modelValue !== selectedLLM) {
          setSelectedLLM(modelValue);
        }
      }
    }, [selectedChatbot?.last_model_used?.value, selectedChatbot?.llmModel?.value]);

    // Fetch personalities
    useEffect(() => {
      const fetchPersonalities = async () => {
        if (!params.id) return;

        setIsLoadingPersonalities(true);
        try {
          const personalitiesResponse = await chatbotService.getPersonalitiesByKbId(params.id);
          const data = personalitiesResponse.data || [];
          setPersonalities(data);
          // If no personalities or no 'formal' personality, auto-select won't happen
          // so the combo check effect will never set isCheckingCombo to false
          const hasFormal = data.some((p) => p.personality_name?.toLowerCase() === 'formal');
          if (data.length === 0 || !hasFormal) {
            setIsCheckingCombo(false);
          }
        } catch (error) {
          console.error('Error fetching personalities:', error);
          setPersonalities([]);
          setIsCheckingCombo(false);
        } finally {
          setIsLoadingPersonalities(false);
        }
      };

      fetchPersonalities();
    }, [params.id]);

    // Default to 'Formality' personality when list loads
    useEffect(() => {
      if (personalities.length === 0 || selectedPersonality != null) return;
      const formality = personalities.find(
        (p) => p.personality_name?.toLowerCase() === 'formal'
      );
      if (formality) setSelectedPersonality(formality.id);
    }, [personalities, selectedPersonality]);

    // Fetch personality content when personality is selected
    useEffect(() => {
      const fetchPersonalityContent = async () => {
        if (!selectedPersonality) {
          setPersonalityContent(null);
          setEnableGeneralKnowledge(false);
          setInitialPersonalityValues(null);
          return;
        }

        try {
          const personality = personalities.find(p => p.id === selectedPersonality);
          if (personality?.personality_content) {
            const nextPersonalityContent = personality.personality_content || '';
            const nextCreativityLevel =
              personality.creativity_level != null
                ? Math.round(personality.creativity_level * 100)
                : 50;
            const nextEnableGeneralKnowledge =
              personality.open_to_internet != null
                ? Boolean(personality.open_to_internet)
                : Boolean(personality.open_to_internet_knowledge);
            setPersonalityContent(nextPersonalityContent);
            setCreativityLevel(nextCreativityLevel);
            setEnableGeneralKnowledge(nextEnableGeneralKnowledge);
            setInitialPersonalityValues({
              personalityContent: nextPersonalityContent,
              creativityLevel: nextCreativityLevel,
              enableGeneralKnowledge: nextEnableGeneralKnowledge,
            });
          } else {
            // If personality_content is not in the list, fetch the full personality details
            const personalityDetails = await chatbotService.getPersonalityById(selectedPersonality);
            const nextPersonalityContent = personalityDetails.data?.personality_content || '';
            const nextCreativityLevel =
              personalityDetails.data?.creativity_level != null
                ? Math.round(personalityDetails.data.creativity_level * 100)
                : 50;
            const nextEnableGeneralKnowledge =
              personalityDetails.data?.open_to_internet != null
                ? Boolean(personalityDetails.data.open_to_internet)
                : Boolean(personalityDetails.data?.open_to_internet_knowledge);
            setPersonalityContent(nextPersonalityContent);
            setCreativityLevel(nextCreativityLevel);
            setEnableGeneralKnowledge(nextEnableGeneralKnowledge);
            setInitialPersonalityValues({
              personalityContent: nextPersonalityContent,
              creativityLevel: nextCreativityLevel,
              enableGeneralKnowledge: nextEnableGeneralKnowledge,
            });
          }
        } catch (error) {
          console.error('Error fetching personality content:', error);
          setPersonalityContent(null);
          setCreativityLevel(50);
          setEnableGeneralKnowledge(false);
          setInitialPersonalityValues(null);
        }
      };

      fetchPersonalityContent();
    }, [selectedPersonality, personalities]);

    // Check deployment combo when personality or LLM changes
    useEffect(() => {
      const checkCombo = async () => {
        if (!selectedPersonality || !selectedLLM || !params.id) {
          setNrMatches(0);
          return;
        }

        try {
          const response = await deploymentService.checkDeploymentCombo(
            params.id,
            selectedPersonality,
            typeof selectedLLM === 'string' ? selectedLLM : selectedLLM
          );
          setNrMatches(response.data?.nr_matches || 0);
        } catch (error) {
          console.error('Error checking deployment combo:', error);
          setNrMatches(0);
        } finally {
          setIsCheckingCombo(false);
        }
      };

      checkCombo();
    }, [selectedPersonality, selectedLLM, params.id]);
  if (showFirstTimeLoader || isCheckingCombo) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-v3-black-lightest dark:bg-gray-900 z-10">
        <DLoading show={true} />
      </div>
    );
  }

  return (
    <PageContainer className="min-h-0">
      <LayoutRightSidebar
      fitSidebarToViewport
      previewButtonLabel="Preview"
      mainClassName="h-full min-h-0"
      RightSidebar={() => {
        return (
          <div className='h-full min-h-0 w-full flex flex-col bubble-preview-min-height:justify-center bubble-preview-min-height:preview:h-auto'>
            <div data-tour-id="preview-bubble" className="flex-1 min-h-0 flex flex-col w-full bubble-preview-min-height:flex-none">
            {/* Hide disclaimer on mobile/tablet (below preview breakpoint) */}
            <div className={clsx(
              'bg-v3-green-primary text-center mx-v3-sm font-inter-variable rounded-tl-v3-md rounded-tr-v3-md max-w-[392px] w-full',
              'hidden preview:flex flex-shrink-0'
            )}>
              <p className='py-v3-xs text-v3-h11 font-medium text-center text-[#fff] w-full'>Live Preview - Test Your Agent Configuration</p>
            </div>
            <div
              className="flex-1 h-full min-h-0 w-full flex flex-col sm:max-w-[424px] bubble-preview-min-height:max-h-[728px] bubble-preview-min-height:preview:flex-none bubble-preview-min-height:preview:h-auto"
            >
              <BubblePreviewWrapper
                showLauncher={false}
                config={{
                  ...customizationData,
                  access_token: auth?.access_token,
                  home_tab_enabled: false,
                  remove_watermark: true,
                  disclaimer_enabled: true,
                  disclaimer_text: "All conversations within the dashboard consume credits.",
                  model_type: selectedLLM ? (typeof selectedLLM === 'string' ? selectedLLM : selectedLLM?.value || selectedLLM) : undefined,
                  llmModel: selectedLLM ? (typeof selectedLLM === 'string' ? selectedLLM : selectedLLM?.value || selectedLLM) : undefined,
                  behaviour_instructions: personalityContent || customizationData?.behaviour_instructions,
                  creativity_level: (creativityLevel / 100).toFixed(2),
                  open_to_internet_knowledge: enableGeneralKnowledge,
                }}
                customizationData={customizationData}
                bubbleProps={{
                  type: "chatbot",
                  chatbotName: customizationData?.display_name || customizationData?.name || 'Dante AI',
                  initialShouldFetchCustomization: false,
                  isInApp: true,
                  isPreviewMode: !selectedPersonality || !selectedLLM,
                  rightSidebar: true,
                  onSendMessage: handleMessageSent,
                  isDashboardPreview: true,
                  kb_id: params.id,
                }}
                applyBannerOffset={false}
              />
            </div>
            </div>
          </div>
        )
      }}
      >
        <div className="h-full min-h-0 overflow-y-auto no-scrollbar">
          <PageHeader breadcrumbItems={[{ label: 'Preview' }]} title={selectedChatbot?.knowledge_base?.knowledge_base_name} />
          {showExplainerCard && (
            <DismissableExplainerCard
              copy="This is your AI agent. Chat with it to see how it responds to your customers. Every change you make updates in real time."
              onDismiss={dismissExplainerCard}
              className="mb-v3-sm"
            />
          )}
          <div className="pt-v3-sm pb-v3-md flex flex-col items-center gap-v3-sm">
              <div className='flex items-end justify-between gap-v3-xs w-full' data-tour-id="preview-personality">
                <Select
                  size="md"
                  label="Personality"
                  options={personalities.map((personality) => ({
                    value: personality.id,
                    label: personality.personality_name,
                  }))}
                  placeholder={isLoadingPersonalities ? 'Loading...' : 'Select a personality'}
                  className='h-10'
                  wrapperClassName='min-w-0'
                  listButtonClassName={clsx('!text-v3-h8 !font-normal', 'truncate', personalityError ? '!border-v3-red-dark' : '!border-v3-black-inactive')}
                  chevronIconClassName='text-v3-purple-primary'
                  value={selectedPersonality}
                  onChange={(option) => {
                    setSelectedPersonality(option);
                    setPersonalityError('');
                  }}
                  error={personalityError}
                  canHaveError={false}
                />
                <Link
                  label="See all"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open(`/chatbot/${params.id}/personality`, '_blank');
                  }}
                  icon={<ArrowUpRight className='group-hover:text-v3-purple-medium'/>}
                  className="flex-shrink-0"
                />
              </div>
              <div className="w-full" data-tour-id="preview-llm">
                <LLMSelectorSimple
                  label="LLM"
                  currentModelType={selectedChatbot?.llm_model_type}
                  onModelChange={(model) => {
                    setSelectedLLM(model.value);
                  }}
                  shouldHaveDropdownWidth={false}
                />
              </div>
              <Textarea
                label="Behavior Instructions"
                value={personalityContent || ''}
                onChange={handleBehaviorInstructionsChange}
                placeholder="Write a detailed prompt outlining your desired AI Chatbot behavior..."
                rows={13}
                wrapperClassName="w-full"
                helperText={
                  <>
                    Instructions for how the bot responds. <button onClick={handleGetHelpClick} className="text-v3-purple-primary hover:text-v3-purple-medium">Get Help</button> from the AI assistant or connect with a team member.
                  </>
                }
              />
              <div className="w-full">
                <label className="!text-v3-black-max text-v3-h11 font-medium">
                  Creativity Level
                </label>
                <div className="flex flex-col items-start gap-v3-sm self-stretch pt-v3-xs">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={creativityLevel}
                    onChange={handleCreativityChange}
                    className="creativity-range-input w-full"
                    style={{
                      background: `linear-gradient(to right, var(--v3-purple-primary) ${creativityLevel}%, var(--v3-card-border) ${creativityLevel}%)`,
                    }}
                  />
                  <div className="flex justify-between items-center self-stretch">
                    <div className="flex items-center gap-v3-xs h-5">
                      <span className="text-v3-black-max font-inter-variable text-xs font-medium leading-4">
                        More Consistent
                      </span>
                      <DTooltip
                        content="Choose 'More Consistent' for similar responses to repeat questions."
                        position="right"
                        className="size-5"
                      >
                        <IconWrapper size={20}>
                          <InfoIcon className="text-v3-blue-primary" />
                        </IconWrapper>
                      </DTooltip>
                    </div>
                    <div className="flex items-center gap-v3-xs h-5">
                      <span className="text-v3-black-max font-inter-variable text-xs font-medium leading-4">
                        More Varied
                      </span>
                      <DTooltip
                        content="Choose 'More Varied' for dynamic responses to repeat questions."
                        position="right"
                        className="size-5"
                      >
                        <IconWrapper size={20}>
                          <InfoIcon className="text-v3-blue-primary" />
                        </IconWrapper>
                      </DTooltip>
                    </div>
                  </div>
                </div>
              </div>
              <InfoBanner
                variant="info"
                label={
                  !selectedPersonality || !selectedLLM
                    ? "This configuration of Personality and LLM is not used in any deployments"
                    : nrMatches === 0
                    ? "This configuration of Personality and LLM is not used in any deployments"
                    : `This configuration of Personality and LLM is used in ${nrMatches} deployment${nrMatches > 1 ? 's' : ''}`
                }
              />
            </div>
            <PageFooterActions
                leftActions={hasPersonalityChanges ? [{
                  label: 'Save Personality',
                  variant: 'secondary',
                  size: 'lg',
                  onClick: handleSavePersonality,
                  loading: isSavingPersonality,
                  loadingLabel: 'Saving...',
                  disabled: !selectedPersonality || isSavingPersonality,
                }] : []}
                rightActions={[{
                    label: nrMatches > 0 ? 'Go to Deployments' : 'Create New Deployment',
                    variant: nrMatches > 0 ? 'secondary' : 'primary',
                    size: 'lg',
                    disabled: !selectedPersonality || !selectedLLM || isSavingPersonality,
                    onClick: () => {
                      if (nrMatches > 0) {
                        navigate(`/chatbot/${params.id}/deployments`, {
                          state: {
                            personality_id: selectedPersonality,
                            model_type: typeof selectedLLM === 'string' ? selectedLLM : selectedLLM
                          }
                        });
                      } else {
                        navigate(`/chatbot/${params.id}/deployment/add`, {
                          state: {
                            personality_id: selectedPersonality,
                            model_type: typeof selectedLLM === 'string' ? selectedLLM : selectedLLM
                          }
                        });
                      }
                    }
                }]}
            />
          </div>
      </LayoutRightSidebar>
    </PageContainer>
  )
}

export default Preview